import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tag, TrendingUp, MapPin, Home, Loader2, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SoldPropertiesPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/properties/sold/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch sold stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <Card className="border-red-200 bg-gradient-to-r from-red-50 to-orange-50 mb-8">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-red-500 mr-2" />
          <span className="text-slate-600">Chargement...</span>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const formatCurrency = (val) => (val || 0).toLocaleString('fr-FR') + ' \u20ac';

  return (
    <Card className="border-red-200 bg-gradient-to-r from-red-50 to-orange-50 mb-8" data-testid="sold-properties-panel">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-xl bg-red-100 flex items-center justify-center">
            <Tag className="h-7 w-7 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Biens Vendus</h3>
            <p className="text-slate-600 text-sm">
              {stats.total_sold} bien{stats.total_sold > 1 ? 's' : ''} vendu{stats.total_sold > 1 ? 's' : ''} dans le Club
            </p>
          </div>
        </div>

        {/* Key Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded-lg p-3 text-center border border-red-200">
            <div className="text-2xl font-bold text-red-600">{stats.total_sold}</div>
            <p className="text-xs text-slate-500">Vendus</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center border border-orange-200">
            <div className="text-lg font-bold text-orange-600">{formatCurrency(stats.total_value)}</div>
            <p className="text-xs text-slate-500">Volume total</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center border border-amber-200">
            <div className="text-lg font-bold text-amber-600">{formatCurrency(stats.average_price)}</div>
            <p className="text-xs text-slate-500">Prix moyen</p>
          </div>
        </div>

        {/* By City */}
        {Object.keys(stats.by_city || {}).length > 0 && (
          <div className="bg-white rounded-lg p-3 mb-4 border border-slate-200">
            <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <MapPin className="w-4 h-4" /> Par ville
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_city).sort((a, b) => b[1] - a[1]).map(([city, count]) => (
                <Badge key={city} className="bg-slate-100 text-slate-700 border-slate-200">
                  {city} ({count})
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* By Type */}
        {Object.keys(stats.by_type || {}).length > 0 && (
          <div className="bg-white rounded-lg p-3 mb-4 border border-slate-200">
            <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <Home className="w-4 h-4" /> Par type
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_type).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                <Badge key={type} className="bg-blue-100 text-blue-700 border-blue-200">
                  {type} ({count})
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Recent Sold */}
        {stats.recent_sold?.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <p className="text-sm font-semibold text-slate-700 p-3 border-b border-slate-100 flex items-center gap-1">
              <TrendingUp className="w-4 h-4" /> Derniers biens vendus
            </p>
            <div className="max-h-48 overflow-y-auto">
              {stats.recent_sold.map((p, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-slate-50 last:border-0 hover:bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{p.title || 'Sans titre'}</p>
                    <p className="text-xs text-slate-500">{p.ville} | {p.owner_name}</p>
                  </div>
                  <span className="text-sm font-bold text-red-600 whitespace-nowrap ml-2">
                    {formatCurrency(p.price)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {stats.total_sold === 0 && (
          <div className="text-center py-6">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">Aucun bien vendu pour le moment</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SoldPropertiesPanel;
